import { accountexternal } from 'src/shared/services/client/client';
import { AvailablePackages, CruftsClubPackage } from '../models/cruftsclub';

const PHASE_SUFFIXES = /_(?:PHASE_ONE|PHASE_TWO)$/;

/** Strip PHASE_ONE / PHASE_TWO suffix from package codes for matching. */
export function normalizePackageCode(code: string): string {
  if (!code) return '';
  return code.replace(PHASE_SUFFIXES, '');
}

/** Map normalized package code to AvailablePackages key. */
export function getPackageKeyFromCode(code: string): keyof AvailablePackages | null {
  const normalized = normalizePackageCode(code);
  switch (normalized) {
    case 'KC_STARTER':
      return 'starter';
    case 'KC_PLUS':
      return 'plus';
    case 'KC_UNLIMITED':
      return 'ultimate';
    default:
      return null;
  }
}

const MEMBERSHIP_CACHE_KEY = 'joii_membership_cache';

interface MembershipCache {
  hasActiveMembership: boolean;
  checkedAt: number;
}

export function getMembershipCache(): MembershipCache | null {
  try {
    const raw = localStorage.getItem(MEMBERSHIP_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MembershipCache;
  } catch {
    return null;
  }
}

export function setMembershipCache(hasActiveMembership = false): void {
  try {
    const value: MembershipCache = {
      hasActiveMembership,
      checkedAt: Date.now(),
    };
    localStorage.setItem(MEMBERSHIP_CACHE_KEY, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
}

// Returns the rossette package icon for each crufts package.
export function getCruftsPackageIcon(packageCode: CruftsClubPackage | string) {
  const basePath = 'assets/svgs/';
  switch (packageCode as CruftsClubPackage) {
    case 'KC_STARTER_PHASE_ONE':
    case 'KC_STARTER_PHASE_TWO':
      return `${basePath}crufts-club-basic-rossette.svg`;
    case 'KC_PLUS_PHASE_ONE':
    case 'KC_PLUS_PHASE_TWO':
      return `${basePath}crufts-club-standard-rossette.svg`;
    case 'KC_UNLIMITED':
      return `${basePath}crufts-club-premium-rossette.svg`;
    default:
      return '';
  }
}

export function formatEntitlementName(entitlement: Partial<{ name: string; count: number; period: string }>) {
  switch (entitlement.name) {
    case 'Symptom Checker Enabled':
      return 'Symptom checker';
    case 'AI Triage':
      return 'Emergency triage';
    case 'All Consultations':
      return `${entitlement.count || 'Unlimited'} chat/video consultations ${entitlement.period && entitlement.count ? `per ${entitlement.period}` : ''}`;
    default:
      return entitlement.name;
  }
}

export function sortEntitlements(entitlements: accountexternal.GetPackageByCodeResponse) {
  const order = ['Symptom Checker Enabled', 'AI Triage', 'All Consultations'];
  entitlements.entitlements = entitlements.entitlements?.sort((a, b) => {
    const aIndex = order.indexOf(a.name);
    const bIndex = order.indexOf(b.name);
    return aIndex - bIndex;
  });
  return entitlements;
}

const CRUFTSCLUB_LOOKUP_ERROR_MESSAGES: Array<{ match: string | RegExp; message: string }> = [
  {
    match: 'cannot re-apply an expired package, expects new external id',
    message:
      'This Crufts Club plan has already been linked to a Joii account. If you think this is an error, tell us at contact@joiipetcate.com',
  },
  {
    match: 'user not found',
    message: "We couldn't find your account. Please sign in and try again.",
  },
  {
    match: 'details not recognised',
    message:
      "We couldn't find a Crufts Club plan with those details. Please check your name, email and postcode and try again.",
  },
  {
    match: "we don't recognise the package code:",
    message:
      "We don't recognise this Crufts Club plan. If you think this is an error, contact us at contact@joiipetcare.com",
  },
  {
    match: 'corresponding package not found for code:',
    message: "We're having trouble linking this plan. Please try again or contact us at contact@joiipetcare.com",
  },
  {
    match: 'Invalid user id format',
    message: 'Something went wrong with your session. Please sign in again and try again.',
  },
  {
    match: 'User not created',
    message: 'Please complete your account setup and try again.',
  },
  {
    match: 'request body required',
    message: 'Please enter your Crufts Club details and try again.',
  },
  {
    match: 'invalid request',
    message: 'Please check your details (name, email and postcode) and try again.',
  },
  {
    match: 'invalid name, name cannot contain numbers',
    message: 'Please enter a valid name (no numbers).',
  },
  {
    match: 'invalid email',
    message: 'Please enter a valid email address.',
  },
  {
    match: 'invalid post code',
    message: 'Please enter a valid postcode.',
  },
  {
    match:
      'This plan has already been linked to a Joii account. If you think this is an error, tell us at contact@joiipetcare.com',
    message:
      'This Crufts Club plan has already been linked to a Joii account. If you think this is an error, tell us at contact@joiipetcare.com',
  },
  {
    match: 'No active plans found for crufts club.',
    message:
      "We couldn't find an active Crufts Club plan with those details. Please contact us at contact@joiipetcare.com",
  },
];

const CRUFTSCLUB_LOOKUP_ERROR_DEFAULT = 'Something went wrong. Please try again.';

/**
 * Maps ApplyMembership / lookup API error messages to user-friendly Crufts Club messages.
 * Pass the lookup error from the entitlements store (or the raw error from applyMembership).
 */
export function getCruftsClubLookupErrorMessage(error: unknown): string {
  const raw = error == null ? '' : error instanceof Error ? error.message : String(error).trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  for (const { match, message } of CRUFTSCLUB_LOOKUP_ERROR_MESSAGES) {
    if (typeof match === 'string') {
      if (lower.includes(match.toLowerCase())) return message;
    } else if (match.test(raw)) {
      return message;
    }
  }
  return CRUFTSCLUB_LOOKUP_ERROR_DEFAULT;
}
